import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMode } from "@/context/ModeContext";
import axios from "axios";
import { API } from "@/config";
import Sidebar from "@/components/Sidebar";
import { Users, Calendar, DollarSign, TrendingUp, Gift, Info } from "lucide-react";

import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import { MobileHeader } from "@/components/MobileHeader";
import { formatRupees } from "../utils/currency";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import PropTypes from 'prop-types';
import { useUserRole } from "../hooks/useUserRole";
import { getFinancialScope } from "../utils/accessPolicy";

export default function Dashboard({ token, onLogout }) {
  const navigate = useNavigate();
  const doctorId = localStorage.getItem('user_id');
  const [motivationalMessage, setMotivationalMessage] = useState("");
  const [showMotivation, setShowMotivation] = useState(false);

  const { activeMode } = useMode();
  const { isReceptionistOnly, memberships } = useUserRole();
  const financialScope = getFinancialScope(activeMode, memberships, isReceptionistOnly);
  const showSimplifiedDashboard = !financialScope.canAccessFinancials;

  const financialClinicIdsKey = [...financialScope.includedClinicIds].sort().join("|");

  const stats = useLiveQuery(async () => {
    if (!doctorId) {
      console.log("Dashboard: doctorId is missing");
      return null;
    }
    console.log("Dashboard querying stats for:", doctorId);

    // 1. Get user's own appointments (same visibility logic as Appointments.jsx)
    const ownApps = await db.appointments.where('doctor_id').equals(doctorId).toArray();

    // 2. Get clinics user is a member of
    const memberships = await db.clinic_members
      .where('user_id').equals(doctorId)
      .filter(m => m.is_active !== false)
      .toArray();
    const allowedClinicIds = memberships.map(m => m.clinic_id);
    const fullAccessClinicIds = memberships
      .filter(m => ['admin', 'owner', 'receptionist'].includes(m.role))
      .map(m => m.clinic_id);

    // 3. Get clinic appointments
    let clinicApps = [];
    if (allowedClinicIds.length > 0) {
      clinicApps = await db.appointments
        .filter(a => a.clinic_id && allowedClinicIds.includes(a.clinic_id))
        .toArray();
    }

    // 4. Merge and deduplicate appointments
    const allAppsMap = new Map();
    ownApps.forEach(app => allAppsMap.set(app.id, app));
    clinicApps.forEach(app => {
      if (!allAppsMap.has(app.id)) allAppsMap.set(app.id, app);
    });
    const allAppointments = Array.from(allAppsMap.values());

    // Fetch other data
    const allPatients = await db.patients.filter(p => !p.deleted).toArray();
    const allCharges = await db.facility_charges.toArray();
    const allLinks = await db.patient_doctor_links.where('user_id').equals(doctorId).toArray();
    const myPatientIds = new Set(allLinks.map(l => l.patient_id));

    // 2. Filter Appointments based on Mode
    // Dashboard always shows user's OWN appointments/earnings - filter by doctor_id first
    let filteredAppointments = allAppointments.filter(a => a.doctor_id === doctorId);

    if (activeMode === 'personal') {
      // Personal: My appointments with no clinic OR personal clinic
      filteredAppointments = filteredAppointments.filter(a => !a.clinic_id || a.clinic_id === 'personal');
    } else if (activeMode !== 'everything') {
      // Specific Clinic: My appointments in this clinic only
      filteredAppointments = filteredAppointments.filter(a => a.clinic_id === activeMode);
    }
    // In 'everything' mode, we already have all MY appointments (filtered above)

    // 3. Filter Patients based on Mode
    let filteredPatients = allPatients;
    if (activeMode === 'personal') {
      filteredPatients = allPatients.filter(p => myPatientIds.has(p.id));
    } else if (activeMode !== 'everything') {
      // Clinic Mode: Patients who have at least one appointment in this clinic
      const patientIdsInClinic = new Set(filteredAppointments.map(a => a.patient_id));
      filteredPatients = allPatients.filter(p => patientIdsInClinic.has(p.id));
    }

    // 4. Filter FacilityCharges based on Mode
    // Create appointment map for clinic filtering and status checking
    const aptMap = new Map(allAppointments.map(a => [a.id, a]));
    // Create set of MY appointment IDs (regardless of clinic)
    const myAppointmentIds = new Set(allAppointments.filter(a => a.doctor_id === doctorId).map(a => a.id));

    // Base filter: facility charges for MY appointments (payable), where I'm associated_doctor (payer), 
    // OR where my clinic is receiving (receivable)
    let filteredCommissions = allCharges.filter(c =>
      myAppointmentIds.has(c.appointment_id) ||
      c.associated_doctor_id === doctorId ||
      fullAccessClinicIds.includes(c.clinic_id)
    );

    // Additional mode-based filtering
    if (activeMode === 'personal') {
      filteredCommissions = filteredCommissions.filter(c => {
        const apt = aptMap.get(c.appointment_id);
        const effectiveClinicId = (apt && apt.clinic_id) ? apt.clinic_id : c.clinic_id;
        return !effectiveClinicId || effectiveClinicId === 'personal';
      });
    } else if (activeMode !== 'everything') {
      // Specific clinic mode
      filteredCommissions = filteredCommissions.filter(c => {
        const apt = aptMap.get(c.appointment_id);
        const effectiveClinicId = (apt && apt.clinic_id) ? apt.clinic_id : c.clinic_id;
        return effectiveClinicId === activeMode;
      });
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Calculate start of current calendar week (Sunday)
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday = 0

    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    let totalEarnings = 0;
    let monthAppointments = 0;
    let todayCount = 0;
    let weekCount = 0;

    const clinicRoleMap = new Map(memberships.map(m => [m.clinic_id, m.role]));

    filteredAppointments.forEach(apt => {
      const aptDate = new Date(apt.date_time);
      const aptDay = new Date(aptDate.getFullYear(), aptDate.getMonth(), aptDate.getDate());

      // Count earnings from completed appointments this month
      if (apt.status === 'completed' && aptDay >= startOfMonth) {
        const clinicRole = apt.clinic_id ? clinicRoleMap.get(apt.clinic_id) : null;
        const isPersonal = !apt.clinic_id || apt.clinic_id === 'personal';
        if (isPersonal || clinicRole === 'admin' || clinicRole === 'consultant') {
          totalEarnings += apt.fee_amount;
        }
      }

      // Count non-cancelled appointments for time-based stats
      if (apt.status !== 'cancelled') {
        if (aptDay >= startOfMonth) monthAppointments++;
        if (aptDay.getTime() === today.getTime()) todayCount++;
        if (aptDay >= startOfWeek) weekCount++;
      }
    });

    // Filter facility_charges by appointment date (not created_at) for consistency with appointments
    let monthFilteredCommissions = filteredCommissions.filter(c => {
      // Use appointment_date field (enriched during sync) for filtering
      const aptDate = c.appointment_date ? new Date(c.appointment_date) : null;
      if (!aptDate) return false; // Skip facility_charges without appointment_date
      return aptDate >= startOfMonth;
    });

    const includedClinicIds = new Set(financialScope.includedClinicIds);
    const isChargeInScope = charge => {
      const appointment = aptMap.get(charge.appointment_id);
      if (appointment) {
        const isPersonal = !appointment.clinic_id || appointment.clinic_id === "personal";
        return isPersonal
          ? financialScope.includePersonal
          : includedClinicIds.has(appointment.clinic_id);
      }
      return Boolean(charge.clinic_id && includedClinicIds.has(charge.clinic_id));
    };
    monthFilteredCommissions = monthFilteredCommissions.filter(isChargeInScope);

    // Payable facility_charges (where I am the associated_doctor = I'm being charged) - only for completed appointments
    const payableCommissions = monthFilteredCommissions.filter(c => {
      if (c.associated_doctor_id !== doctorId) return false; // I'm not the one being charged
      const apt = aptMap.get(c.appointment_id);
      // Use appointment status from enriched data or local appointment
      const status = apt?.status || c.appointment_status;
      return status === 'completed';
    });
    const facilityChargePayable = payableCommissions.filter(c => c.paid_status === 'pending').reduce((sum, c) => sum + c.charge_amount, 0);
    const chargePaid = payableCommissions.filter(c => c.paid_status === 'paid').reduce((sum, c) => sum + c.charge_amount, 0);

    // Receivable facility_charges (where I am NOT the associated_doctor — I'm the clinic/payee)
    // These are charges from my appointments where someone else is being charged
    const referralCommissions = doctorId ? monthFilteredCommissions.filter(c => {
      if (c.associated_doctor_id === doctorId) return false; // I'm the payer, not receiver
      // Use appointment_status from facility_charge (enriched during sync) since we may not have the appointment
      const apt = aptMap.get(c.appointment_id);
      const status = apt?.status || c.appointment_status;
      return status === 'completed';
    }) : [];
    const referralEarningsTotal = referralCommissions.reduce((sum, c) => sum + c.charge_amount, 0);
    const referralEarningsPending = referralCommissions.filter(c => c.paid_status === 'pending').reduce((sum, c) => sum + c.charge_amount, 0);
    const referralEarningsReceived = referralCommissions.filter(c => c.paid_status === 'paid').reduce((sum, c) => sum + c.charge_amount, 0);

    return {
      total_earnings: totalEarnings + referralEarningsTotal,
      direct_earnings: totalEarnings,
      patients_treated: filteredPatients.length,
      month_appointments: monthAppointments,
      today_appointments: todayCount,
      week_appointments: weekCount,
      charge_payable: facilityChargePayable,
      charge_paid: chargePaid,
      referral_earnings_total: referralEarningsTotal,
      referral_earnings_pending: referralEarningsPending,
      referral_earnings_received: referralEarningsReceived
    };
  }, [doctorId, activeMode, financialScope.canAccessFinancials, financialScope.includePersonal, financialClinicIdsKey]);

  useEffect(() => {
    fetchMotivation();
  }, []);

  const fetchMotivation = async () => {
    try {
      const response = await axios.get(`${API}/motivational-message`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.enabled && response.data.message) {
        setMotivationalMessage(response.data.message);
        setShowMotivation(true);
      }
    } catch {
      console.log("Offline or error fetching motivation");
    }
  };

  if (!stats) {
    return (
      <div className="page-container">
        <Sidebar active="dashboard" onLogout={onLogout} />
        <div className="main-content">
          <div style={{ textAlign: 'center', padding: '4rem', color: '#64748b' }} data-testid="dashboard-loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="dashboard-page">
      <Sidebar active="dashboard" onLogout={onLogout} />
      <div className="main-content">
        {/* Desktop Header */}
        <div className="header hidden md:flex">
          <h1 data-testid="dashboard-title">Dashboard</h1>
        </div>

        {/* Mobile Header */}
        <MobileHeader title="Dashboard" />

        {showMotivation && motivationalMessage && (
          <div className="motivational-card" data-testid="motivational-message">
            <h2>Keep Going!</h2>
            <p>{motivationalMessage}</p>
          </div>
        )}

        <div className="stats-grid">
          {!showSimplifiedDashboard && (
            <div className="stat-card clickable" onClick={() => navigate("/reports")}>
              <div className="stat-card-header">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  This Month&apos;s Earnings
                  {activeMode && activeMode !== 'everything' && activeMode !== 'personal' && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info size={16} className="text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p>Your personal earnings from this clinic. To see clinic-wide revenue, use the Clinic Dashboard.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </h3>
                <div className="stat-icon" style={{ background: '#d1fae5' }}>
                  <DollarSign size={24} color="#065f46" />
                </div>
              </div>
              <div className="stat-value" data-testid="total-earnings">{formatRupees(stats?.total_earnings || 0)}</div>
              <div className="stat-label">Current month&apos;s total</div>
            </div>
          )}

          <div className="stat-card clickable" onClick={() => navigate("/patients")}>
            <div className="stat-card-header">
              <h3>Patients Treated</h3>
              <div className="stat-icon" style={{ background: '#dbeafe' }}>
                <Users size={24} color="#1e40af" />
              </div>
            </div>
            <div className="stat-value" data-testid="patients-treated">{stats?.patients_treated || 0}</div>
            <div className="stat-label">Total unique patients</div>
          </div>

          <div className="stat-card clickable" onClick={() => navigate("/appointments")}>
            <div className="stat-card-header">
              <h3>Appointments</h3>
              <div className="stat-icon" style={{ background: '#fef3c7' }}>
                <Calendar size={24} color="#92400e" />
              </div>
            </div>
            <div className="stat-value" data-testid="month-appointments">{stats?.month_appointments || 0}</div>
            <div className="stat-label">{stats?.week_appointments || 0} this week</div>
          </div>

          {/* Facility Charge Payable - Only show if > 0 */}
          {!showSimplifiedDashboard && (stats?.charge_payable > 0 || stats?.charge_paid > 0) && (
            <div className="stat-card clickable" onClick={() => navigate("/facility-charges")}>
              <div className="stat-card-header">
                <h3>Charges Due</h3>
                <div className="stat-icon" style={{ background: '#fef3c7' }}>
                  <TrendingUp size={24} color="#92400e" />
                </div>
              </div>
              <div className="stat-value" data-testid="facility_charge-payable">{formatRupees(stats?.charge_payable || 0)}</div>
              <div className="stat-label">{formatRupees(stats?.charge_paid || 0)} already paid</div>
            </div>
          )}

          {/* Referral Earnings - Only show if > 0 */}
          {!showSimplifiedDashboard && stats?.referral_earnings_total > 0 && (
            <div className="stat-card clickable" onClick={() => navigate("/facility-charges")}>
              <div className="stat-card-header">
                <h3>Consultation Earnings</h3>
                <div className="stat-icon" style={{ background: '#d1fae5' }}>
                  <Gift size={24} color="#059669" />
                </div>
              </div>
              <div className="stat-value" data-testid="referral-earnings">{formatRupees(stats?.referral_earnings_total || 0)}</div>
              <div className="stat-label">{formatRupees(stats?.referral_earnings_pending || 0)} pending / {formatRupees(stats?.referral_earnings_received || 0)} received</div>
            </div>
          )}
        </div>

        <div className="card">
          <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '600' }}>Quick Stats</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
            <div className="clickable" onClick={() => navigate("/appointments")} style={{ padding: '0.5rem', borderRadius: '8px', transition: 'background 0.2s' }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#0ea5e9' }}>{stats?.today_appointments || 0}</div>
              <div style={{ color: '#64748b', fontSize: '0.875rem' }}>Today&apos;s Appointments</div>
            </div>
            <div className="clickable" onClick={() => navigate("/appointments")} style={{ padding: '0.5rem', borderRadius: '8px', transition: 'background 0.2s' }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#10b981' }}>{stats?.week_appointments || 0}</div>
              <div style={{ color: '#64748b', fontSize: '0.875rem' }}>This Week</div>
            </div>
            <div className="clickable" onClick={() => navigate("/appointments")} style={{ padding: '0.5rem', borderRadius: '8px', transition: 'background 0.2s' }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#8b5cf6' }}>{stats?.month_appointments || 0}</div>
              <div style={{ color: '#64748b', fontSize: '0.875rem' }}>This Month</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

Dashboard.propTypes = {
  token: PropTypes.string,
  onLogout: PropTypes.func
};